import axiosV2Instance from './axiosV2Instance';

export interface Estimate {
  id: number;
  siteId: number;
  createdBy: number;
  name: string;
  description: string;
  dateCreated: string;
  dateUpdated?: string;
  isClosed: boolean;
  site?: {
    id: number;
    name: string;
    closed: boolean;
  };
  createdByUser?: {
    id: number;
    name: string;
  };
}

export interface EstimateItem {
  id: number;
  estimateId: number;
  typeOfWorkId?: number;
  name: string;
  unit?: string;
  quantity?: number;
  pricePerUnit?: number;
  total?: number;
  sectionId?: number; // Ссылка на секцию (любого уровня)
  sortOrder: number;
  isTotalRow: boolean;
  typeOfWork?: {
    id: number;
    name: string;
    closed: boolean;
    section: string;
  };
}

/** Пункт сметы с данными об использовании в контрактах (для создания контракта) */
export interface EstimateItemWithUsage extends EstimateItem {
  contractedQuantity?: number;
  contractedGrossAmount?: number;
  contractedNetAmount?: number;
  remainingQuantity?: number;
  remainingAmount?: number;
}

export interface EstimateWithItems extends Estimate {
  items: EstimateItem[];
}

export interface CreateEstimateRequest {
  name: string;
  description: string;
  siteId?: number; // Опционально, так как устанавливается из маршрута на сервере
  dateCreated?: string;
  isClosed?: boolean;
}

export interface UpdateEstimateRequest {
  name: string;
  description: string;
  dateCreated?: string;
  dateUpdated?: string;
  isClosed?: boolean;
}

export interface CreateEstimateItemRequest {
  name: string;
  unit?: string;
  quantity?: number;
  pricePerUnit?: number;
  total?: number;
  sectionId?: number; // Ссылка на секцию (любого уровня)
  sortOrder?: number;
  isTotalRow?: boolean;
  typeOfWorkId?: number;
}

export interface EstimateStructure {
  id: number;
  estimateId: number;
  name: string; // Название секции любого уровня
  parentId?: number; // Ссылка на родительскую секцию (null для корневых секций)
  sortOrder: number;
  children?: EstimateStructure[]; // Дочерние секции (рекурсивная структура)
}

export interface CreateEstimateStructureRequest {
  name: string; // Название секции
  parentId?: number; // ID родительской секции (null для корневых секций)
  sortOrder?: number;
}

class EstimatesApi {
  /**
   * Получить все сметы для объекта
   */
  async getSiteEstimates(siteId: number): Promise<Estimate[]> {
    try {
      const response = await axiosV2Instance.get<Estimate[]>(
        `/sites/${siteId}/estimates`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching estimates for site ${siteId}:`, error);
      throw error;
    }
  }

  /**
   * Получить смету с пунктами
   */
  async getEstimate(siteId: number, estimateId: number): Promise<EstimateWithItems> {
    try {
      const response = await axiosV2Instance.get<EstimateWithItems>(
        `/sites/${siteId}/estimates/${estimateId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching estimate ${estimateId} for site ${siteId}:`, error);
      throw error;
    }
  }

  /**
   * Создать новую смету
   */
  async createEstimate(siteId: number, data: CreateEstimateRequest): Promise<Estimate> {
    try {
      const response = await axiosV2Instance.post<Estimate>(
        `/sites/${siteId}/estimates`,
        data
      );
      return response.data;
    } catch (error) {
      console.error(`Error creating estimate for site ${siteId}:`, error);
      throw error;
    }
  }

  /**
   * Обновить смету
   */
  async updateEstimate(
    siteId: number,
    estimateId: number,
    data: UpdateEstimateRequest
  ): Promise<Estimate> {
    try {
      const response = await axiosV2Instance.put<Estimate>(
        `/sites/${siteId}/estimates/${estimateId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating estimate ${estimateId} for site ${siteId}:`, error);
      throw error;
    }
  }

  /**
   * Удалить смету
   */
  async deleteEstimate(siteId: number, estimateId: number): Promise<void> {
    try {
      await axiosV2Instance.delete(`/sites/${siteId}/estimates/${estimateId}`);
    } catch (error) {
      console.error(`Error deleting estimate ${estimateId} for site ${siteId}:`, error);
      throw error;
    }
  }

  /**
   * Получить пункты сметы
   */
  async getEstimateItems(siteId: number, estimateId: number): Promise<EstimateItem[]> {
    try {
      const response = await axiosV2Instance.get<EstimateItem[]>(
        `/sites/${siteId}/estimates/${estimateId}/items`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching items for estimate ${estimateId}:`, error);
      throw error;
    }
  }

  /**
   * Получить пункты сметы для создания контракта (с остатками и данными об уже заключённых контрактах)
   */
  async getEstimateItemsForContract(siteId: number, estimateId: number): Promise<EstimateItemWithUsage[]> {
    try {
      const response = await axiosV2Instance.get<EstimateItemWithUsage[]>(
        `/sites/${siteId}/estimates/${estimateId}/items/for-contract`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching items for contract, estimate ${estimateId}:`, error);
      throw error;
    }
  }

  /**
   * Добавить пункт в смету
   */
  async addEstimateItem(
    siteId: number,
    estimateId: number,
    item: CreateEstimateItemRequest
  ): Promise<EstimateItem> {
    try {
      const response = await axiosV2Instance.post<EstimateItem>(
        `/sites/${siteId}/estimates/${estimateId}/items`,
        item
      );
      return response.data;
    } catch (error) {
      console.error(`Error adding item to estimate ${estimateId}:`, error);
      throw error;
    }
  }

  /**
   * Обновить пункт сметы
   */
  async updateEstimateItem(
    siteId: number,
    estimateId: number,
    itemId: number,
    item: CreateEstimateItemRequest
  ): Promise<EstimateItem> {
    try {
      const response = await axiosV2Instance.put<EstimateItem>(
        `/sites/${siteId}/estimates/${estimateId}/items/${itemId}`,
        item
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating item ${itemId} in estimate ${estimateId}:`, error);
      throw error;
    }
  }

  /**
   * Удалить пункт сметы
   */
  async deleteEstimateItem(
    siteId: number,
    estimateId: number,
    itemId: number
  ): Promise<void> {
    try {
      await axiosV2Instance.delete(
        `/sites/${siteId}/estimates/${estimateId}/items/${itemId}`
      );
    } catch (error) {
      console.error(`Error deleting item ${itemId} from estimate ${estimateId}:`, error);
      throw error;
    }
  }

  /**
   * Получить структуру сметы (секции и подсекции)
   */
  async getEstimateStructure(siteId: number, estimateId: number): Promise<EstimateStructure[]> {
    try {
      const response = await axiosV2Instance.get<EstimateStructure[]>(
        `/sites/${siteId}/estimates/${estimateId}/structure`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching structure for estimate ${estimateId}:`, error);
      throw error;
    }
  }

  /**
   * Создать секцию в структуре сметы
   */
  async createSection(
    siteId: number,
    estimateId: number,
    data: CreateEstimateStructureRequest
  ): Promise<EstimateStructure> {
    try {
      const response = await axiosV2Instance.post<EstimateStructure>(
        `/sites/${siteId}/estimates/${estimateId}/structure/sections`,
        data
      );
      return response.data;
    } catch (error) {
      console.error(`Error creating section for estimate ${estimateId}:`, error);
      throw error;
    }
  }

  /**
   * Создать дочернюю секцию (подсекцию) в структуре сметы
   */
  async createChildSection(
    siteId: number,
    estimateId: number,
    parentSectionId: number,
    data: CreateEstimateStructureRequest
  ): Promise<EstimateStructure> {
    try {
      const response = await axiosV2Instance.post<EstimateStructure>(
        `/sites/${siteId}/estimates/${estimateId}/structure/sections/${parentSectionId}/children`,
        data
      );
      return response.data;
    } catch (error) {
      console.error(`Error creating child section for parent ${parentSectionId}:`, error);
      throw error;
    }
  }

  /**
   * Обновить элемент структуры сметы
   */
  async updateEstimateStructure(
    siteId: number,
    estimateId: number,
    structureId: number,
    data: CreateEstimateStructureRequest
  ): Promise<EstimateStructure> {
    try {
      const response = await axiosV2Instance.put<EstimateStructure>(
        `/sites/${siteId}/estimates/${estimateId}/structure/${structureId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating structure ${structureId}:`, error);
      throw error;
    }
  }

  /**
   * Удалить элемент структуры сметы
   */
  async deleteEstimateStructure(
    siteId: number,
    estimateId: number,
    structureId: number
  ): Promise<void> {
    try {
      await axiosV2Instance.delete(
        `/sites/${siteId}/estimates/${estimateId}/structure/${structureId}`
      );
    } catch (error) {
      console.error(`Error deleting structure ${structureId}:`, error);
      throw error;
    }
  }

  /**
   * Создать структуру сметы пакетно (секции и подсекции)
   */
  async createEstimateStructureBatch(
    siteId: number,
    estimateId: number,
    structures: CreateEstimateStructureRequest[]
  ): Promise<{ addedCount: number }> {
    try {
      const response = await axiosV2Instance.post<{ addedCount: number }>(
        `/sites/${siteId}/estimates/${estimateId}/structure/batch`,
        structures
      );
      return response.data;
    } catch (error) {
      console.error(`Error creating structure batch for estimate ${estimateId}:`, error);
      throw error;
    }
  }
}

export const estimatesApi = new EstimatesApi();

